/**
 * Etiquetas, tonos y cálculos de presentación del módulo de metas.
 *
 * Los catálogos (`goal_type`, `priority`, `status`) son los mismos que valida el
 * backend, así que aquí viven sus etiquetas en español y su color semántico.
 */

import type { MetricFormat } from '@/lib/metric-format';
import { toNumber } from '@/lib/utils';

import type {
  Goal,
  GoalPerformance,
  GoalPriority,
  GoalStatus,
  GoalType,
} from '../metas.types';

/** Tipo de atajo para los tonos que aceptan `Badge` y `TONE_CLASSES`. */
export type MetaTone = 'brand' | 'emerald' | 'amber' | 'rose' | 'ink';

/** Variante del componente `Badge` para cada tono. */
export type MetaBadgeVariant = 'brand' | 'success' | 'warning' | 'danger' | 'neutral';

export const TONO_A_BADGE: Record<MetaTone, MetaBadgeVariant> = {
  brand: 'brand',
  emerald: 'success',
  amber: 'warning',
  rose: 'danger',
  ink: 'neutral',
};

export const TIPO_LABELS: Record<GoalType, string> = {
  volume: 'Volumen',
  amount: 'Monto',
  transactions: 'Transacciones',
  fuel_grade: 'Producto',
  performance: 'Desempeño',
  sales: 'Ventas',
  productivity: 'Productividad',
  quality: 'Calidad',
  other: 'Otro',
};

export const ESTADO_LABELS: Record<GoalStatus, string> = {
  active: 'Activa',
  completed: 'Cumplida',
  paused: 'En pausa',
  cancelled: 'Cancelada',
};

export const ESTADO_TONES: Record<GoalStatus, MetaTone> = {
  active: 'brand',
  completed: 'emerald',
  paused: 'amber',
  cancelled: 'ink',
};

export const PRIORIDAD_LABELS: Record<GoalPriority, string> = {
  critical: 'Crítica',
  high: 'Alta',
  medium: 'Media',
  low: 'Baja',
};

export const PRIORIDAD_TONES: Record<GoalPriority, MetaTone> = {
  critical: 'rose',
  high: 'amber',
  medium: 'brand',
  low: 'ink',
};

export const DESEMPENO_LABELS: Record<GoalPerformance, string> = {
  completed: 'Cumplida',
  on_track: 'En camino',
  warning: 'Atención',
  behind: 'Atrasada',
  expired: 'Vencida',
};

export const DESEMPENO_TONES: Record<GoalPerformance, MetaTone> = {
  completed: 'emerald',
  on_track: 'brand',
  warning: 'amber',
  behind: 'rose',
  expired: 'ink',
};

export const DESEMPENO_BARRAS: Record<GoalPerformance, string> = {
  completed: 'bg-emerald-500',
  on_track: 'bg-brand-500',
  warning: 'bg-amber-500',
  behind: 'bg-rose-500',
  expired: 'bg-ink-400',
};

/** Tipos y prioridades en el orden en que se ofrecen en los formularios. */
export const TIPOS_ORDENADOS: readonly GoalType[] = [
  'volume',
  'amount',
  'transactions',
  'productivity',
  'sales',
  'quality',
  'performance',
  'fuel_grade',
  'other',
];

export const PRIORIDADES_ORDENADAS: readonly GoalPriority[] = ['critical', 'high', 'medium', 'low'];

export const ESTADOS_ORDENADOS: readonly GoalStatus[] = ['active', 'completed', 'paused', 'cancelled'];

/** Unidades sugeridas según el tipo de meta (el backend no impone una lista). */
export const UNIDADES_POR_TIPO: Partial<Record<GoalType, string>> = {
  volume: 'Galones',
  amount: 'Soles',
  transactions: 'Transacciones',
  productivity: 'Galones',
  sales: 'Soles',
  quality: 'Porcentaje',
};

export function tipoLabel(tipo: GoalType | string | null | undefined): string {
  return TIPO_LABELS[tipo as GoalType] ?? String(tipo ?? '—');
}

export function estadoLabel(estado: GoalStatus | string | null | undefined): string {
  return ESTADO_LABELS[estado as GoalStatus] ?? String(estado ?? '—');
}

export function prioridadLabel(prioridad: GoalPriority | string | null | undefined): string {
  return PRIORIDAD_LABELS[prioridad as GoalPriority] ?? String(prioridad ?? '—');
}

/** Avance acotado a 0-100 para barras de progreso. */
export function avance(goal: Pick<Goal, 'current_value' | 'target_value' | 'progress_percentage'>): number {
  const porcentaje =
    goal.progress_percentage !== undefined && goal.progress_percentage !== null
      ? toNumber(goal.progress_percentage)
      : 0;

  if (porcentaje > 0) return Math.min(Math.max(porcentaje, 0), 100);

  const objetivo = toNumber(goal.target_value);
  if (objetivo <= 0) return 0;

  return Math.min(Math.max((toNumber(goal.current_value) / objetivo) * 100, 0), 100);
}

/** Nombre del responsable o "General" cuando la meta no está asignada. */
export function responsable(goal: Pick<Goal, 'first_name' | 'last_name' | 'user_id'>): string {
  const nombre = `${goal.first_name ?? ''} ${goal.last_name ?? ''}`.trim();

  return nombre || (goal.user_id ? `Usuario ${goal.user_id}` : 'General');
}

/** Valor con su unidad de medida (ej. "43,806.87 Galones"). */
export function valorConUnidad(valor: string | number | null | undefined, unidad: string | null): string {
  const numero = toNumber(valor);

  return `${numero.toLocaleString('es-PE', { maximumFractionDigits: 2 })}${unidad ? ` ${unidad}` : ''}`;
}

/** Días restantes legibles. */
export function diasRestantes(dias: number | null | undefined): string {
  const total = Number(dias ?? 0);

  if (!Number.isFinite(total)) return '—';
  if (total > 1) return `${total} días`;
  if (total === 1) return '1 día';
  if (total === 0) return 'Vence hoy';

  return `Vencida hace ${Math.abs(total)} días`;
}

/**
 * Días que faltan para el cierre de la meta.
 *
 * El detalle (`GET /goals/:id`) no calcula `days_remaining`, así que cuando no
 * viene se estima con la fecha de fin; el backend sí lo envía en el listado.
 */
export function diasRestantesDe(
  goal: Pick<Goal, 'days_remaining' | 'end_date'>,
): number {
  if (typeof goal.days_remaining === 'number' && Number.isFinite(goal.days_remaining)) {
    return goal.days_remaining;
  }

  const fin = new Date(goal.end_date);
  if (Number.isNaN(fin.getTime())) return 0;

  const hoy = new Date();
  const dias = Math.ceil((fin.getTime() - hoy.getTime()) / 86_400_000);

  return Number.isFinite(dias) ? dias : 0;
}

/**
 * Formato de métrica que corresponde a la unidad de medida de la meta.
 *
 * El backend guarda la unidad como texto libre ("Galones", "Soles", "%"), así que
 * se traduce al formato de presentación para no mostrar `S/` en volúmenes.
 */
export function formatoDeUnidad(unidad: string | null | undefined): MetricFormat {
  const texto = (unidad ?? '').trim().toLowerCase();

  if (texto.includes('soles') || texto.includes('s/') || texto.includes('monet')) {
    return 'currency';
  }

  if (texto.includes('galon') || texto.includes('gal')) return 'gallons';
  if (texto.includes('%') || texto.includes('porcentaje')) return 'percent';

  return 'number';
}

/** Signo y tono de una variación porcentual (positiva = mejor). */
export function tonoVariacion(valor: number | string | null | undefined): MetaTone {
  const numero = toNumber(valor);

  if (numero > 0) return 'emerald';
  if (numero < 0) return 'rose';

  return 'ink';
}
